/**
 * Custom hook to detect online/offline status
 * Uses both navigator.onLine and API health check for reliability
 */

import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(true); // Default to online
  const [wasOffline, setWasOffline] = useState(false);

  // Check actual connectivity by pinging backend
  const checkConnectivity = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API}/api/menu`, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store'
      });
      
      clearTimeout(timeoutId);
      return response.ok || response.status === 401 || response.status === 403;
    } catch (error) {
      // Network error or timeout
      return false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const handleOnline = async () => {
      console.log('[Network] Browser reports online, verifying...');
      const actuallyOnline = await checkConnectivity();
      if (mounted && actuallyOnline) {
        console.log('[Network] Connection confirmed');
        setIsOnline(true);
        setWasOffline(true);
        setTimeout(() => mounted && setWasOffline(false), 5000);
      }
    };

    const handleOffline = () => {
      console.log('[Network] Connection lost');
      if (mounted) setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check - trust navigator.onLine initially but verify if it says offline
    const initialCheck = async () => {
      if (!navigator.onLine) {
        // Browser says offline, but let's verify
        const actuallyOnline = await checkConnectivity();
        if (mounted) setIsOnline(actuallyOnline);
      } else {
        // Browser says online, trust it
        if (mounted) setIsOnline(true);
      }
    };
    initialCheck();

    return () => {
      mounted = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnectivity]);

  return { isOnline, wasOffline };
};
